/* ============================================================
   About us — who the practice is.

   Deliberately narrow. Four pages already exist that a company page
   would otherwise duplicate, and duplication here is not merely
   wasteful — it is how a site starts contradicting itself:

     /about     how figures are derived, provenance, coverage limits
     /privacy   what happens to data, and the legal basis for it
     /terms     what the tools are and are not, as a matter of scope
     /          what the product does, for someone deciding to try it

   So this page answers only the question none of those do — who is
   behind it, on what standing, and how to engage — and links to
   whichever page owns each of the rest. Where a fact appears here and
   elsewhere (the address, the incorporation status) it is read from
   SITE rather than retyped, so the two cannot drift apart.
   ============================================================ */

import { SITE, ABOUT } from '../data/site.js';
import { TOOLS } from '../data/tools.js';
import { DOMAINS } from '../data/domains.js';
import { BENCHMARKS } from '../data/benchmarks.js';
import { REGULATIONS, statusOf } from '../data/regulations.js';
import { html, raw } from '../shared/html.js';
import { icon } from '../shared/icons.js';

/* Counted, never typed. A scope claim has to be true on the day someone
   reads it, not the day it was written — and "in force" moves on its own
   because it is computed from effective dates. */
function coverage() {
  const live = TOOLS.filter((t) => t.status === 'live').length;
  const questions = DOMAINS.reduce((n, d) => n + d.questions.length, 0);
  const inForce = REGULATIONS.filter((r) => statusOf(r) === 'in-force').length;
  return [
    { value: live, label: 'diagnostic tools', note: 'free, no account' },
    { value: DOMAINS.length, label: 'scorecard domains', note: `${questions} questions, weighted` },
    { value: Object.keys(BENCHMARKS).length, label: 'corridors benchmarked', note: 'sourced and dated' },
    { value: REGULATIONS.length, label: 'instruments tracked', note: `${inForce} in force today` }
  ];
}

/* Each of these points somewhere else rather than summarising it. A
   one-line summary of a privacy notice is a fifth version of the privacy
   notice, and the one nobody updates. */
const ELSEWHERE = [
  {
    path: '/about',
    label: 'Method & sources',
    hint: 'How every figure is derived, where it comes from, and what it cannot tell you'
  },
  {
    path: '/privacy',
    label: 'Privacy Notice',
    hint: 'What happens to anything you type, and the legal basis for it'
  },
  { path: '/terms', label: 'Terms of Use', hint: 'What these tools are, and what they are not' }
];

export function AboutUsPage() {
  const page = document.createElement('div');
  page.className = 'container-narrow page animate-fade-in';

  page.innerHTML = html`
    <header class="page-head">
      <h1>${ABOUT.heading}</h1>
      <p>${ABOUT.lead} ${ABOUT.leadMore}</p>
    </header>

    <section class="section">
      <h2 class="section-title">The practice</h2>
      <div class="card about-prose">
        <p>
          Kanda works one corridor properly rather than the continent thinly. The Northern
          Corridor — Mombasa inland through Nairobi to the Uganda, Rwanda and eastern DRC
          markets — is where the regulation, the tariffs and the failure modes are modelled in
          depth. Other corridors appear where a figure can be sourced honestly, and are marked
          as such where it cannot.
        </p>
        <p>
          The people it is built for are the ones who already carry the risk: transporters and
          freight forwarders pricing lanes, shippers and manufacturers who discover the landed
          cost after the invoice, and the lenders and investors who have to decide whether an
          operator's numbers hold. The tools ask the questions those three groups argue about,
          and answer them the same way each time.
        </p>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">What is covered today</h2>
      <div class="about-coverage">
        ${coverage().map(
          (c) => html`<div class="about-coverage-item">
            <b class="num">${c.value}</b>
            <span class="about-coverage-label">${c.label}</span>
            <small>${c.note}</small>
          </div>`
        )}
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">Working with us</h2>
      <div class="card about-prose">
        <p>
          The tools are free, they stay free, and they are not a trial of anything. They run on
          your device, they answer the question they were built for, and you owe nothing for
          them. That is the whole arrangement.
        </p>
        <p>
          What they do not do is act. A scorecard that puts your demurrage at three times the
          corridor median has told you something true and left you exactly where you were. The
          work below is what happens next, and it is the only thing here that is charged for.
        </p>
      </div>

      <div class="fee-band">
        <div class="fee-item">
          <b class="fee-figure num">KES 360,000+</b>
          <span class="fee-name">Corridor diagnostic</span>
          <small>
            One corridor or trade lane, examined properly: landed cost, demurrage exposure,
            emissions baseline or readiness. Fixed fee against a written scope, quoted before
            anything starts.
          </small>
        </div>
        <div class="fee-item">
          <b class="fee-figure num">KES 1,250,000+</b>
          <span class="fee-name">Structuring programme</span>
          <small>
            Licensing, SEZ or EPZ positioning, capital stack, corporate structure. Longer work,
            paid against defined submissions rather than elapsed time.
          </small>
        </div>
        <div class="fee-item">
          <b class="fee-figure num">KES 540,000+</b>
          <span class="fee-name">Own-brand toolkit, a year</span>
          <small>
            The whole toolkit carrying an operator's or association's name, colours and logo,
            including on every export. Nothing to build and nothing to host.
          </small>
        </div>
      </div>

      <div class="card" style="margin-top: var(--ca-space-4);">
        <p class="about-note">
          ${raw(icon('info', 14))} Fees exclude VAT and are quoted in writing before work
          begins. Expenses are capped as a share of the fee rather than billed open-ended.
          Where a saving can be measured against a baseline the tools have already produced,
          part of the fee can be tied to it — ask, because it is not offered by default.
        </p>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">Standing and contact</h2>
      <div class="card">
        <dl class="about-facts">
          <div>
            <dt>Registered name</dt>
            <dd>${SITE.registeredEntity}</dd>
          </div>
          <div>
            <dt>Address</dt>
            <dd>${SITE.registeredAddress}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd><a href="mailto:${SITE.email}">${SITE.email}</a></dd>
          </div>
          <div>
            <dt>Sister practice</dt>
            <dd>
              <a href="${SITE.sister.url}" target="_blank" rel="noopener noreferrer">
                ${SITE.sister.name}</a
              >
              — ${SITE.sister.descriptor}, at the same address.
            </dd>
          </div>
        </dl>
        <p class="about-note">
          ${raw(icon('info', 14))} The company is mid-incorporation, so there is no registration
          number to quote yet. That is stated rather than left blank, and it will be filled in
          here when it exists.
        </p>
      </div>
    </section>

    <section class="section">
      <h2 class="section-title">The rest is on its own page</h2>
      <p class="section-lede">
        Everything below is documented properly elsewhere. Summarising it here would create a
        second version to keep in step, and eventually two answers to the same question.
      </p>
      <div class="footer-learn-grid about-elsewhere">
        ${ELSEWHERE.map(
          (l) => html`<a class="fl-item" href="${l.path}">
            <b>${l.label}</b><small>${l.hint}</small>
          </a>`
        )}
      </div>
    </section>
  `;

  // No click wiring here: the router already delegates on document for
  // any same-origin href it has a route for. A second handler would
  // preventDefault twice and duplicate the history entry.
  return page;
}
